import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';

const marketHeaderCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}::slotted(.wayfinding),::slotted([slot='wayfinding']){font-weight:var(--core-type-paragraph-20-weight);font-size:var(--core-type-paragraph-20-size);font-family:var(--core-type-paragraph-20-font-family);line-height:var(--core-type-paragraph-20-leading);letter-spacing:var(--core-type-paragraph-20-tracking);text-transform:var(--core-type-paragraph-20-case)}::slotted(.wayfinding) b,::slotted(.wayfinding) strong,::slotted([slot='wayfinding']) b,::slotted([slot='wayfinding']) strong{font-weight:var(--core-type-semibold-20-weight)}:host{--header-animation-duration:0.2s;display:block}::slotted(*){margin:0}::slotted(.wayfinding),::slotted([slot='wayfinding']){display:block;color:var(--core-text-20-color)}::slotted([slot='wayfinding']){margin-bottom:8px}:host([show-navigation]) ::slotted([slot='compact']:not(.wayfinding)){font-weight:var(--core-type-heading-20-weight) !important;font-size:var(--core-type-heading-20-size) !important;font-family:var(--core-type-heading-20-font-family) !important;line-height:var(--core-type-heading-20-leading) !important;letter-spacing:var(--core-type-heading-20-tracking) !important;text-transform:var(--core-type-heading-20-case) !important;}nav{padding-right:var(--header-main-text-horizontal-spacing)}menu{display:none;grid-auto-flow:column;align-items:start;margin:0;padding-left:var(--header-main-text-horizontal-spacing);-moz-column-gap:var(--button-group-spacing);column-gap:var(--button-group-spacing)}:host([show-actions]) menu{display:grid}.grid{display:none}:host([show-actions]) .grid{display:grid;grid-template-columns:auto minmax(max-content, 1fr)}:host([show-navigation]) .grid{display:grid;grid-template-columns:minmax(max-content, 1fr) auto minmax(max-content, 1fr)}.navigation{display:none}:host([show-navigation]) .navigation{display:flex}.actions{display:flex;place-content:flex-end}.compact{display:flex;flex-direction:column;place-content:center}::slotted(p){margin-top:var(--header-bottom-padding-size)}::slotted(p[slot='compact']){display:none}:host([show-navigation]) .compact{overflow:hidden;text-align:center;opacity:0;visibility:hidden;transition:opacity var(--header-animation-duration) ease-in-out,\n    visibility 0s linear var(--header-animation-duration)}:host([show-navigation]) .compact ::slotted([slot='compact']){overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.heading{display:block;transition:opacity var(--header-animation-duration) ease-in-out, visibility 0s}:host([show-navigation]) .heading{margin-top:var(--header-nav-icon-button-spacing-buttom-size)}:host([show-actions]:not([show-navigation])) .heading{display:none}:host([show-navigation][compact]) .compact{opacity:1;visibility:visible;transition:opacity var(--header-animation-duration) ease-in-out, visibility 0s}:host([show-navigation][compact]) .heading{opacity:0;visibility:hidden;transition:opacity var(--header-animation-duration) ease-in-out,\n      visibility 0s linear var(--header-animation-duration)}:host(.regular) .heading{width:var(--modal-full-fixed-layout-width);margin-right:auto;margin-left:auto}:host(.wide) .heading{width:1200px;margin-right:auto;margin-left:auto}";

const MarketHeader = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketHeaderNavigate = createEvent(this, "marketHeaderNavigate", 7);
    this.showNavigation = null;
    this.closeButtonAriaLabel = 'Close';
    this.disableCloseButton = false;
    this.compact = false;
    this.showActions = false;
  }
  handleCompact() {
    if (this.compact) {
      const compact = this.el.shadowRoot.querySelector('.grid .compact');
      const minHeight = Number.parseInt(getComputedStyle(compact).height, 10);
      this.el.style.height = minHeight !== undefined ? `${minHeight}px` : '';
    }
    else {
      this.el.style.height = '';
    }
  }
  handleSlottedNavigation() {
    // If there is custom navigation passed in, we need to
    // attach event handlers to the slotted elements manually
    const customNavEl = this.el.querySelector('[slot="navigation"]');
    if (customNavEl) {
      this.showNavigation = true;
      customNavEl.addEventListener('click', this.handleSlottedNavClick.bind(this));
    }
  }
  handleSlottedActions() {
    const actions = this.el.querySelector('[slot="actions"]');
    this.showActions = actions ? true : false;
  }
  handleCloseButtonClick(event) {
    this.marketHeaderNavigate.emit({
      event,
      action: 'close',
    });
  }
  handleSlottedNavClick(event) {
    this.marketHeaderNavigate.emit({
      event,
      action: 'custom',
    });
  }
  componentWillLoad() {
    this.checkForNavigation();
    this.cloneDefaultHeadingToCompactHeading();
    // watch for changes in slotted children content
    const observer = new MutationObserver(this.handleMutation.bind(this));
    observer.observe(this.el, { childList: true, characterData: true, subtree: true });
  }
  componentDidRender() {
    // this function requires layout computation,
    // so we need to wait until render to call.
    this.handleCompact();
  }
  handleMutation(mutationList) {
    // We don't want to re-clone the default slot content if
    // the mutation was already the result of the cloning
    // because then we would get into an infinite loop!
    // So we have to look at the mutation data to check if a
    // compact slotted element was added to prevent re-cloning.
    let shouldClone = true;
    mutationList.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.slot === 'compact') {
            shouldClone = false;
          }
        });
      }
    });
    if (shouldClone) {
      this.cloneDefaultHeadingToCompactHeading();
    }
  }
  checkForNavigation() {
    // We want to force the header to be navigable when slotted in modals
    const hasModalParent = this.el.closest(`
      ${getNamespacedTagFor('market-modal-full')},
      ${getNamespacedTagFor('market-modal-partial')}
    `);
    // The same if custom navigation is slotted in
    const hasCustomNavigation = this.el.querySelector('[slot="navigation"]');
    // Only set this if the prop hasn't been passed in manually
    if (this.showNavigation === null) {
      this.showNavigation = hasModalParent || hasCustomNavigation ? true : false;
    }
  }
  cloneDefaultHeadingToCompactHeading() {
    // clear existing compact slotted content
    const compactSlottedContent = this.el.querySelectorAll('[slot="compact"]');
    for (const node of compactSlottedContent) {
      this.el.removeChild(node);
    }
    // clone the wayfinding slot into compact slot
    const wayfindingEl = this.el.querySelector('[slot="wayfinding"]');
    if (wayfindingEl) {
      const clone = wayfindingEl.cloneNode(true);
      clone.slot = 'compact';
      clone.classList.add('wayfinding');
      this.el.appendChild(clone);
    }
    // clone the rest of the default slot content into compact slot
    const defaultSlotContent = [...this.el.children].filter((el) => !el.getAttribute('slot'));
    for (const node of defaultSlotContent) {
      const clone = node.cloneNode(true);
      clone.slot = 'compact';
      this.el.appendChild(clone);
    }
  }
  renderDefaultNavigation() {
    const MarketButtonTagName = getNamespacedTagFor('market-button');
    return (h(MarketButtonTagName, { rank: "secondary", slot: "navigation", "aria-label": this.closeButtonAriaLabel, disabled: this.disableCloseButton, onClick: (event) => this.handleCloseButtonClick(event) }, h("svg", { slot: "icon", width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("g", null, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M0.292894 0.292893C0.683418 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6 4.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L7.41421 6L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L6 7.41421L1.70711 11.7071C1.31658 12.0976 0.683417 12.0976 0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L4.58579 6L0.292894 1.70711C-0.0976306 1.31658 -0.0976306 0.683417 0.292894 0.292893Z" })))));
  }
  render() {
    return (h(Host, { class: "market-header", "show-actions": this.showActions }, h("div", { class: "grid" }, h("div", { class: "navigation" }, h("nav", null, h("slot", { name: "navigation", onSlotchange: () => this.handleSlottedNavigation() }, this.renderDefaultNavigation()))), h("div", { class: "compact" }, h("slot", { name: "compact" })), h("div", { class: "actions" }, h("menu", null, h("slot", { name: "actions", onSlotchange: () => this.handleSlottedActions() })))), h("div", { class: "heading", part: "heading" }, h("slot", { name: "wayfinding" }), h("slot", null))));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "compact": ["handleCompact"]
  }; }
};
MarketHeader.style = marketHeaderCss;

export { MarketHeader as market_header };
